<template>
    <div class="page">
        <NuxtErrorBoundary @error="showError">
            <template v-if="!isEmbedded">
                <a v-if="hasMain" id="skip-to-content" href="#main">{{ $i18n.t('skipToContent') }}</a>
                <div class="page-container">
                    <NuxtLoadingIndicator
                        :throttle="200"
                        :duration="2000"
                        :color="colors.primary"
                    />
                    <DSNotifications />
                    <LayoutMainHeader>
                        <template #center>
                            <LayoutMainNavigationDesktop
                                class="main-nav"
                                :links="navigationOne"
                            />
                        </template>
                        <template v-if="$bp.bps.isDesktop" #right>
                            <LayoutSearchButton />
                            <LayoutQuickLinks :links="layoutData.navigation" />
                            <LayoutUserMenu :is-visible="userMenu" :customer-type="customerType" />
                        </template>
                        <template
                            v-if="navigationOne?.find(item => item.url.includes(routeArray.slice(0, 2).join('/')))?.children"
                            #bottom
                        >
                            <LayoutMainNavigationDesktop
                                v-if="routeArray.length > 1"
                                class="main-nav"
                                :links="navigationOne?.find(item => item.url.includes(routeArray.slice(0, 2).join('/')))?.children.map(markSelected)"
                            />
                        </template>
                    </LayoutMainHeader>
                    <DSSidebar class="lenis-ignore">
                        <template #sidebar-right>
                            <LayoutMainNavigationMobile :links="navigationTree" :user-menu="userMenu" />
                        </template>
                    </DSSidebar>
                    <NuxtPage class="page-content" />
                    <Teleport v-if="modal.name === 'emergencyModal'" :to="modal.modalTarget">
                        <div class="ds-modal-content">
                            <div class="head">
                                <div class="head-content">{{ layoutData.emergencyModal.title }}</div>
                                <button class="close-btn" @click="modal.unsetNameAndClose">
                                    <DSIcon
                                        code="close"
                                        size="xs"
                                    />
                                </button>
                            </div>
                            <div class="body">
                                <DSCFText :content="layoutData.emergencyModal.text" class="ds-container text-center" />
                            </div>
                            <div class="foot">
                                <div class="text-center">
                                    <button
                                        class="ds-btn ds-btn--primary"
                                        @click="modal.unsetNameAndClose"
                                    >
                                        {{ $t('emergencyModal.close') }}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </Teleport>
                    <LayoutSearchModal />
                    <DSModal />
                    <LayoutQuickNavigation />
                </div>
                <LayoutFooter
                    :icons="layoutData?.icons"
                    :items="layoutData?.footerLinks"
                    :download="layoutData?.downloadLinks"
                    :some="layoutData?.someLinks"
                    :extras="layoutData?.extraLinks"
                />
            </template>
            <template v-else>
                <NuxtPage
                    :transition="{
                        onBeforeEnter,
                    }"
                />
            </template>
            <LayoutAnnouncer />
        </NuxtErrorBoundary>
    </div>
</template>
<script setup>
import { useLayoutStore } from '~dsc/stores/cms/layout';
import { usePageStore } from '~dsc/stores/cms/page';
import {
    ref,
    computed,
    onMounted,
    provide,
    useNuxtApp,
    useI18n,
    useRoute,
    useAnalytics,
    showError,
} from '#imports';
import { storeToRefs } from 'pinia';
import * as websiteTokens from '~dsc/assets/scss/themes/st1-website/_export.module.scss';
import 'lenis/dist/lenis.css';
import { useModalStore } from '~dsc/stores/modal';
import Lenis from 'lenis';
import gtmPerLocale from '@@/configuration/gtm.json';

const { finalizePendingLocaleChange, locale } = useI18n({
    useScope: 'global',
    messages: {
        'fi-FI': {
            skipToContent: 'Hyppää sisältöön',
            emergencyModal: {
                close: 'Sulje',
            },
        },
        'en-EU': {
            emergencyModal: {
                close: 'Close',
            },
            skipToContent: 'Go to content',
        },
        'sv-SE': {
            skipToContent: 'Gå till innehållet',
        },
        'no-NO': {
            skipToContent: 'Gå til inneholdet',
        },
    },
});

const { layoutData, navigationTree } = storeToRefs(useLayoutStore());
const { $paths, $bp, $i18n } = useNuxtApp();
const { pageData } = storeToRefs(usePageStore());
const { initGtm } = useAnalytics();
const route = useRoute();
const modal = useModalStore();

await useLayoutStore().layoutLoad();

initGtm({ id: gtmPerLocale[locale.value] });

const colors = {
    'primary': websiteTokens.default['color-primary-500'],
};

const hasMain = computed(() => {
    if (!window) return true;

    return document.querySelector('main#main');
});

const isEmbedded = computed(() => Number(route.query?.embed) === 1);
const firstBlockFullScreen = computed(() => pageData.value?.[0]?.display?.type === 'fullscreen');
const userMenu = computed(() => {
    const [, section] = route.path.split('/');

    if (locale.value === 'sv-SE' && section !== 'foretag') return false;
    if (locale.value === 'no-NO' && section !== 'bedrift') return false;
    if (locale.value === 'en-EU') return false;

    return true;
});

const layout = ref({
    name: 'st1-website',
    theme: firstBlockFullScreen.value === true ? 'clear' : 'white',
    sidebars: {
        left: false,
        right: false,
    },
    button: {
        ctaIcon: 'arrow-right',
    },
});

const routeArray = computed(() => {
    const routeArray = route.path.split('/');

    return routeArray.filter(value => value !== '' && value !== 'preview');
});

const markSelected = item => {
    const urlArray = item.url?.split('/') || [];
    const currentLevel = routeArray.value.slice(0, urlArray.length);

    return {
        ...item,
        active: urlArray.join('/') === currentLevel.join('/'),
    };
};

const navigationOne = computed(() => {
    if (!navigationTree.value.map(item => item.url)
        .includes(routeArray.value[0])) {
        return navigationTree.value?.[0]?.children.map(markSelected) ?? [];
    }

    return navigationTree.value.find(item => item.url === routeArray.value[0])
        ?.children
        .map(markSelected) ?? [];
});
const customerType = computed(() => $paths.getPath('b2b') === `/${routeArray.value[0]}` ? 'B2B' : 'B2C');

const onBeforeEnter = async () => {
    await finalizePendingLocaleChange();
    usePageStore()
        .present();
    layout.value.theme = firstBlockFullScreen.value === true ? 'clear' : 'white';
};

onMounted(() => {
    if (layoutData?.value?.emergencyModal) modal.setNameAndOpen('emergencyModal');
    const ignoredScrollContainers = ['station-locator', 'lenis-ignore', 'ds-select', 'ds-table'];
    new Lenis({
        autoRaf: true,
        prevent: node => ignoredScrollContainers.some(selector => node.classList.contains(selector)),
    });
});

provide('layout', layout);
provide('dsIconConfig', computed(() => ({ debug: false, preferSVG: true, defaultNamespace: 'default-fill' })));
</script>
<style lang="scss">
header {
    z-index: 5;
}

.header .bottom {
    transition: margin .275s;
}

.header.is-sticky {
    .content {
        padding-top: .625rem;
        padding-bottom: .75rem;
    }

    .bottom:not(:empty) {
        margin-top: 1.375rem;
    }
}

.page-content {
    position: relative;
    display: flex;
    flex-flow: row wrap;
    overflow: hidden;
    padding-top: 9rem;

    .hero-banner.fullscreen:first-child {
        margin-top: -9rem;
    }

    .station-locator:last-child {
        padding-bottom: 0;
    }

    @include respond-to('at least desktop') {
        .station-locator:first-child {
            margin-top: -4rem;
        }
    }
}

.page {
    display: grid;
    min-height: 100vh;
    max-width: 100%;
    grid-template-columns: 1fr;
    grid-template-rows: minmax(calc(100vh - 10.75rem), 1fr);
}

.page-container {
    width: 100%;
    overflow-x: hidden;
    min-height: calc(100vh - 4rem);
    position: relative;
    z-index: 1;

    > .notifications {
        position: fixed;
        height: calc(100vh - 20rem);
        width: 90vw;
        padding-block: 1rem;
        max-width: var(--page-max-width);
        transform: translate(-50%, -50%);
        left: 50%;
        top: 50%;
        z-index: 6;
        pointer-events: none;

        .ds-notification {
            pointer-events: auto;
        }
    }
}

.logo {
    display: block;

    object {
        pointer-events: none;
        height: 42px;
        width: auto;
        image-rendering: optimizeQuality;
    }
}

#skip-to-content {
    position: absolute;
    opacity: 0;
    background: var(--color-base-white);
    color: var(--color-base-black);
    font-weight: 700;

    &:focus {
        opacity: 1;
        display: block;
        text-align: center;
        padding: 1em;
        width: 100%;
        top: 0;
        z-index: 10;
    }
}

@include respond-to('at most small desktop') {
    .page {
        grid-template-rows: minmax(calc(100vh - 4rem), 1fr);
    }

    .page-content {
        min-height: calc(100vh - 4rem);
        padding-top: 4rem;

        .hero-banner.fullscreen:first-child {
            margin-top: -4rem;
        }
    }
}
</style>
